import { useState } from 'react';
import { hasNoLogo, needsWhiteBg } from '@siliconx/shared/fmv/manufacturer-logo';

/**
 * Manufacturer brand logo + name. Logos are static SVGs bundled in
 * public/mfr-logos/, keyed by manufacturer id (the slug). Missing file →
 * onError collapses the image, leaving just the name (which is always shown
 * anyway, so the logo is purely additive).
 *
 * The NO_LOGO / NEEDS_WHITE_BG exception lists live in
 * @siliconx/shared/fmv/manufacturer-logo — shared with apps/operations'
 * identical component and apps/fmv's vanilla renderer, so all three surfaces
 * agree on which brands have no mark or need a light backing.
 */

export function ManufacturerLogo({
  id,
  name,
  size = 14,
  slot = false,
  className = '',
}: {
  id: string | null | undefined;
  name: string | null | undefined;
  /** logo height in px; width is auto (capped) since logos vary in aspect ratio */
  size?: number;
  /**
   * Reserve a fixed-width leading box (size*3) so the name text starts at the
   * same x across rows regardless of each logo's width — use in table columns
   * so names left-align. Empty logos still reserve the box.
   */
  slot?: boolean;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const showImg = !!id && !hasNoLogo(id) && !failed;
  const whiteBg = needsWhiteBg(id);
  // px-1.5 (6px * 2) + border (1px * 2) — must come out of the size*3 budget below,
  // or the chip (padding/border included) overflows the reserved slot and crowds
  // the name text next to it (happened with the wide HPE wordmark).
  const CHIP_CHROME = 14;
  const boxWidth = size * 3 - (whiteBg ? CHIP_CHROME : 0);
  const mark = showImg ? (
    // whiteBg chip uses LITERAL white via inline style, not `bg-white` — this app
    // remaps bg-white/border-zinc to --n-* CSS vars that flip to near-black under
    // `.dark` (globals.css @theme), which would make the "white" plate dark. Inline
    // literals bypass the token remap so the card stays white in both themes.
    <span
      className={whiteBg ? 'inline-flex items-center rounded border px-1.5 py-0.5 shadow-sm' : 'contents'}
      style={whiteBg ? { backgroundColor: '#ffffff', borderColor: '#d4d4d8' } : undefined}
    >
      {/* fixed box + object-contain scales the logo to fit without distorting it,
          unlike a fixed height + width:auto + maxWidth (which squishes once clamped) */}
      <span className="inline-flex shrink-0 items-center justify-center" style={{ width: boxWidth, height: size }}>
        <img
          src={`/mfr-logos/${id}.svg`}
          alt=""
          aria-hidden
          onError={() => setFailed(true)}
          className="max-h-full max-w-full object-contain"
        />
      </span>
    </span>
  ) : null;
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      {slot ? (
        <span className="inline-flex shrink-0 items-center" style={{ width: size * 3 }}>
          {mark}
        </span>
      ) : (
        mark
      )}
      {name ?? id ?? '—'}
    </span>
  );
}
